import { resolverRango, type RangoEnUrl, type TokenRango } from './rango-fechas';

/**
 * DESEMPEÑO DE TÉCNICOS — la lógica de la pantalla. PURA.
 *
 * LA PREGUNTA que contesta esta pantalla no es «cuántas garantías tiene Luis»,
 * es **cuántas garantías está dejando cada técnico por cada 10 instalaciones
 * que hace**. El número crudo miente: el que instala 200 veces y deja 8
 * garantías va mejor que el que instala 10 y deja 4, y mirando la columna de
 * garantías parece al revés.
 *
 * Todo lo que se puede probar vive acá: el índice, el semáforo, el orden y la
 * traducción del rango a lo que pide la API. La pantalla solo pinta.
 */

/** Una fila de la tabla: un técnico en el rango elegido. */
export interface FilaDesempeno {
  tecnicoId: string;
  /** Nombre del técnico, como se muestra. */
  tecnico: string;
  /** Instalaciones completadas en el rango. */
  instalaciones: number;
  /** Garantías atendidas en el rango. */
  garantias: number;
  /** Garantías cerradas sin completar la ficha. */
  fichasIncompletas: number;
}

/** El primer campo que exista de la lista, como número. 0 si no hay ninguno. */
function numero(fila: Record<string, unknown>, ...claves: string[]): number {
  for (const c of claves) {
    const v = fila[c];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return 0;
}

/** El primer campo que exista de la lista, como texto. */
function texto(fila: Record<string, unknown>, ...claves: string[]): string | null {
  for (const c of claves) {
    const v = fila[c];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/**
 * Lo que contesta `GET /api/tecnicos/desempeno` → las filas de la tabla. PURO.
 *
 * ⚠️ Es deliberadamente tolerante, y no por gusto: **el endpoint lo está
 * construyendo otra tanda en paralelo** y el nombre exacto de cada campo no
 * está cerrado. Se aceptan los dos envoltorios que usa la API (la lista pelada
 * y `{ items }`) y los nombres razonables de los mismos tres números.
 *
 * Lo que NO hace es inventar: una fila sin técnico se descarta y una respuesta
 * que no se entiende da lista vacía, que la pantalla muestra como "todavía no
 * hay nada que mostrar". Nunca un número traído de la nada.
 */
export function filasDesdeLaApi(crudo: unknown): FilaDesempeno[] {
  const lista = Array.isArray(crudo)
    ? crudo
    : crudo && typeof crudo === 'object' && Array.isArray((crudo as { items?: unknown }).items)
      ? (crudo as { items: unknown[] }).items
      : [];

  const filas: FilaDesempeno[] = [];
  for (const item of lista) {
    if (!item || typeof item !== 'object') continue;
    const fila = item as Record<string, unknown>;
    const tecnicoId = texto(fila, 'tecnicoId', 'employeeId', 'id');
    if (!tecnicoId) continue;
    filas.push({
      tecnicoId,
      tecnico: texto(fila, 'tecnico', 'nombre', 'name') ?? 'Sin nombre',
      instalaciones: numero(fila, 'instalaciones', 'instalacionesCompletadas'),
      garantias: numero(fila, 'garantias', 'garantiasAtendidas'),
      fichasIncompletas: numero(
        fila,
        'fichasIncompletas',
        'garantiasFichaIncompleta',
        'fichaIncompleta',
      ),
    });
  }
  return filas;
}

/**
 * Garantías por cada 10 instalaciones. `null` cuando no hay instalaciones.
 *
 * `null` y no 0: un técnico con 0 instalaciones y 1 garantía daría infinito, y
 * uno con 0 y 0 no dice nada. Decir "no se puede saber" es lo honesto — quizá
 * recién entró, o el rango elegido es muy corto.
 *
 * Un decimal y no más: más precisión acá es falsa precisión.
 */
export function garantiasPor10(instalaciones: number, garantias: number): number | null {
  if (!instalaciones || instalaciones <= 0) return null;
  return Math.round((garantias / instalaciones) * 100) / 10;
}

export type ColorDelIndice = 'verde' | 'ambar' | 'rojo' | 'gris';

/**
 * El semáforo del índice. PURO.
 *
 * El criterio se dijo con números redondos —hasta 1 verde, de 2 a 3 ámbar, de 4
 * para arriba rojo— y eso deja dos huecos: el 1.5 y el 3.5. Se pintan ámbar, que
 * es "mirálo", en vez de dejarlos sin color o darles el verde por descarte.
 */
export function colorDelIndice(indice: number | null): ColorDelIndice {
  if (indice === null) return 'gris';
  if (indice <= 1) return 'verde';
  if (indice < 4) return 'ambar';
  return 'rojo';
}

/** El índice como se lee en la tabla. PURO. */
export function textoDelIndice(indice: number | null): string {
  return indice === null ? 'Sin datos' : String(indice);
}

export type ColumnaDesempeno =
  | 'tecnico'
  | 'instalaciones'
  | 'garantias'
  | 'indice'
  | 'fichasIncompletas';

export type Direccion = 'asc' | 'desc';

export interface OrdenDesempeno {
  columna: ColumnaDesempeno;
  direccion: Direccion;
}

export interface ColumnaDeTabla {
  clave: ColumnaDesempeno;
  etiqueta: string;
  /** Los números van a la derecha; el nombre, a la izquierda. */
  numerica: boolean;
}

/** Las columnas de la tabla, en el orden en que se muestran. */
export const COLUMNAS_DESEMPENO: ColumnaDeTabla[] = [
  { clave: 'tecnico', etiqueta: 'Técnico', numerica: false },
  { clave: 'instalaciones', etiqueta: 'Instalaciones', numerica: true },
  { clave: 'garantias', etiqueta: 'Garantías', numerica: true },
  { clave: 'indice', etiqueta: 'Garantías por cada 10 instalaciones', numerica: true },
  { clave: 'fichasIncompletas', etiqueta: 'Fichas incompletas', numerica: true },
];

/**
 * La tabla abre por el índice, de peor a mejor.
 *
 * La pantalla existe para encontrar al que está dejando más garantías. Si
 * abriera ordenada por nombre habría que buscarlo a ojo, que es exactamente lo
 * que hoy se hace sin esta pantalla.
 */
export const ORDEN_POR_DEFECTO: OrdenDesempeno = { columna: 'indice', direccion: 'desc' };

const CLAVES = new Set<string>(COLUMNAS_DESEMPENO.map((c) => c.clave));

/** El `?orden=` de la URL → el orden de la tabla. Tolerante. PURO. */
export function leerOrden(crudo: string | undefined | null): OrdenDesempeno {
  const [columna, direccion] = (crudo ?? '').split('-');
  // Alguien edita la URL a mano, o llega un enlace de una versión con otra
  // columna: la tabla tiene que abrirse igual, no quedarse en blanco.
  if (!columna || !CLAVES.has(columna)) return ORDEN_POR_DEFECTO;
  if (direccion !== 'asc' && direccion !== 'desc') return ORDEN_POR_DEFECTO;
  return { columna: columna as ColumnaDesempeno, direccion };
}

/** El orden → el `?orden=` de la URL. `undefined` si es el de siempre. PURO. */
export function escribirOrden(orden: OrdenDesempeno): string | undefined {
  if (
    orden.columna === ORDEN_POR_DEFECTO.columna &&
    orden.direccion === ORDEN_POR_DEFECTO.direccion
  ) {
    return undefined;
  }
  return `${orden.columna}-${orden.direccion}`;
}

/**
 * Qué orden queda al tocar el título de una columna. PURO.
 *
 * Al cambiar de columna se arranca por lo más alto en los números (lo que se
 * busca es el máximo) y por la A en el nombre.
 */
export function alternarOrden(actual: OrdenDesempeno, columna: ColumnaDesempeno): OrdenDesempeno {
  if (actual.columna === columna) {
    return { columna, direccion: actual.direccion === 'desc' ? 'asc' : 'desc' };
  }
  return { columna, direccion: columna === 'tecnico' ? 'asc' : 'desc' };
}

/** El valor por el que se compara una fila en una columna. */
function valor(f: FilaDesempeno, columna: ColumnaDesempeno): number {
  if (columna === 'instalaciones') return f.instalaciones;
  if (columna === 'garantias') return f.garantias;
  if (columna === 'fichasIncompletas') return f.fichasIncompletas;
  return garantiasPor10(f.instalaciones, f.garantias) ?? 0;
}

/**
 * Las filas ordenadas. Devuelve una lista nueva. PURO.
 *
 * ⚠️ **Quien no tiene instalaciones queda SIEMPRE último**, se ordene como se
 * ordene. Tratarlo como 0 lo colaría entre los mejores; tratarlo como infinito
 * lo pondría a encabezar la lista de los peores. Las dos cosas son mentira, y
 * las dos se leerían como un juicio sobre alguien que quizá recién entró.
 */
export function ordenarDesempeno(filas: FilaDesempeno[], orden: OrdenDesempeno): FilaDesempeno[] {
  const signo = orden.direccion === 'asc' ? 1 : -1;
  return [...filas].sort((a, b) => {
    const sinDatosA = a.instalaciones <= 0;
    const sinDatosB = b.instalaciones <= 0;
    if (sinDatosA !== sinDatosB) return sinDatosA ? 1 : -1;

    if (orden.columna === 'tecnico') {
      return signo * a.tecnico.localeCompare(b.tecnico, 'es');
    }
    const dif = valor(a, orden.columna) - valor(b, orden.columna);
    // Empate: por nombre, para que la tabla no baile entre recargas.
    return dif !== 0 ? signo * dif : a.tecnico.localeCompare(b.tecnico, 'es');
  });
}

export interface ChipDeRango {
  /** `null` es el chip «Rango…», que abre los dos campos de fecha. */
  token: TokenRango | null;
  etiqueta: string;
}

/**
 * Los tres accesos rápidos del rango.
 *
 * "Hoy" y "Mañana" no están a propósito: un día de instalaciones no alcanza
 * para juzgar a nadie, y ofrecerlo invita a mirar un número que no significa
 * nada.
 */
export const CHIPS_DE_RANGO: ChipDeRango[] = [
  { token: 'semana', etiqueta: 'Esta semana' },
  { token: 'mes', etiqueta: 'Este mes' },
  { token: null, etiqueta: 'Rango…' },
];

/**
 * El rango de la URL → el `?desde&hasta` que pide la API. PURO.
 *
 * Sin nada elegido abre en el mes: es la ventana con la que se mira esto: una
 * semana de instalaciones es muy poca cosa para sacar conclusiones.
 */
export function rangoAConsulta(
  entrada: RangoEnUrl,
  ahora: Date = new Date(),
): { desde: string; hasta: string } {
  const r = resolverRango(entrada, ahora);
  if (r.desde && r.hasta) return { desde: r.desde, hasta: r.hasta };

  const mes = resolverRango({ rango: 'mes' }, ahora);
  return { desde: mes.desde!, hasta: mes.hasta! };
}
